// @ts-nocheck
import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import { api } from '../../utils/api.js';

function SortableItem({ col }) {
  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: col.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: isDragging ? 'var(--color-surface-2)' : 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? '0 4px 16px rgba(0,0,0,0.18)' : 'none',
        position: 'relative',
        zIndex: isDragging ? 999 : 'auto',
      }}
      {...attributes}
      {...listeners}
    >
      <GripVertical size={16} color="var(--color-text-3)" style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 500 }}>
        {col.column_label}
      </span>
    </div>
  );
}

export default function ColumnReorder({ columns, onReorder, onClose }) {
  const [items, setItems]   = useState(columns);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oldIdx = prev.findIndex(c => c.id === active.id);
      const newIdx = prev.findIndex(c => c.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        items.map((col, i) => api.updateCustomColumn(col.id, { display_order: i }))
      );
      onReorder(items);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--color-surface)', borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        width: '100%', maxWidth: 400,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Reorder columns</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <X size={20} color="var(--color-text-2)" />
          </button>
        </div>

        <div style={{
          padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: 8,
          maxHeight: 400, overflowY: 'auto',
        }}>
          {items.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-text-2)', fontSize: 14 }}>
              No custom columns to reorder.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {items.map(col => <SortableItem key={col.id} col={col} />)}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {error && (
          <p style={{ margin: '0 20px 8px', fontSize: 13, color: 'var(--color-red)', fontWeight: 500 }}>
            {error}
          </p>
        )}

        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--color-border)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>
    </div>
  );
}

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8,
  padding: '9px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const secondaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-surface)', color: 'var(--color-text-2)',
  border: '1px solid var(--color-border)', borderRadius: 8,
  padding: '9px 20px', fontWeight: 500, fontSize: 14, cursor: 'pointer',
};
